import { useState, useRef, useEffect, useCallback } from "react";
import {
    MdFormatBold,
    MdFormatItalic,
    MdFormatUnderlined,
    MdFormatListBulleted,
    MdFormatListNumbered,
    MdFormatClear,
} from "react-icons/md";

/**
 * RichTextTopicInput
 * ──────────────────
 * A clean, lightweight Rich Text editor component for syllabus topics.
 * Supports Bold, Italic, Underline, Bullet Lists, Numbered Lists,
 * and Clear Formatting with keyboard shortcuts (Ctrl+B, Ctrl+I, Ctrl+U).
 */
export default function RichTextTopicInput({
    value = "",
    onChange,
    placeholder = "Enter topic description (use toolbar to format: bold, bullets, etc.)",
    minHeight = 44,
}) {
    const editorRef = useRef(null);
    const [focused, setFocused] = useState(false);
    const [activeFormats, setActiveFormats] = useState({
        bold: false,
        italic: false,
        underline: false,
        bullet: false,
        number: false,
    });

    // Strip empty HTML tags to determine if editor is functionally empty
    const isHtmlEmpty = (html) => {
        if (!html) return true;
        const stripped = html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
        return stripped.length === 0;
    };

    // Update active format state based on current cursor/selection
    const updateActiveFormats = useCallback(() => {
        if (!editorRef.current) return;
        try {
            setActiveFormats({
                bold: document.queryCommandState("bold"),
                italic: document.queryCommandState("italic"),
                underline: document.queryCommandState("underline"),
                bullet: document.queryCommandState("insertUnorderedList"),
                number: document.queryCommandState("insertOrderedList"),
            });
        } catch {
            // Ignore if document.queryCommandState is not supported
        }
    }, []);

    // Sync external value to innerHTML when value changes from outside (and not focused)
    useEffect(() => {
        if (editorRef.current && !focused) {
            const currentHTML = editorRef.current.innerHTML;
            if (currentHTML !== value) {
                editorRef.current.innerHTML = value || "";
            }
        }
    }, [value, focused]);

    // Handle input in contentEditable
    const handleInput = () => {
        if (!editorRef.current) return;
        const html = editorRef.current.innerHTML;
        const cleanVal = isHtmlEmpty(html) ? "" : html;
        if (onChange) {
            onChange(cleanVal);
        }
        updateActiveFormats();
    };

    // Execute format command while keeping editor focused
    const exec = (command) => {
        if (editorRef.current) {
            editorRef.current.focus();
        }
        document.execCommand(command, false, null);
        handleInput();
        updateActiveFormats();
    };

    // Keyboard shortcuts
    const handleKeyDown = (e) => {
        if (e.ctrlKey || e.metaKey) {
            if (e.key === "b" || e.key === "B") {
                e.preventDefault();
                exec("bold");
            } else if (e.key === "i" || e.key === "I") {
                e.preventDefault();
                exec("italic");
            } else if (e.key === "u" || e.key === "U") {
                e.preventDefault();
                exec("underline");
            }
        }
    };

    const showPlaceholder = isHtmlEmpty(value) && !focused;

    return (
        <div
            style={{
                border: `1px solid ${focused ? "var(--primary, #003b67)" : "var(--border, #e5e7eb)"}`,
                borderRadius: "var(--radius, 6px)",
                background: "#fff",
                transition: "border-color 0.15s, box-shadow 0.15s",
                boxShadow: focused ? "0 0 0 3px rgba(0, 59, 103, 0.12)" : "none",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                width: "100%",
            }}
        >
            {/* Toolbar */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "2px",
                    padding: "3px 6px",
                    background: "#f8fafc",
                    borderBottom: "1px solid var(--border, #e5e7eb)",
                    userSelect: "none",
                    flexWrap: "wrap",
                }}
            >
                <ToolbarButton
                    active={activeFormats.bold}
                    onClick={() => exec("bold")}
                    title="Bold (Ctrl+B)"
                    icon={<MdFormatBold size={16} />}
                />
                <ToolbarButton
                    active={activeFormats.italic}
                    onClick={() => exec("italic")}
                    title="Italic (Ctrl+I)"
                    icon={<MdFormatItalic size={16} />}
                />
                <ToolbarButton
                    active={activeFormats.underline}
                    onClick={() => exec("underline")}
                    title="Underline (Ctrl+U)"
                    icon={<MdFormatUnderlined size={16} />}
                />
                <div style={{ width: 1, height: 16, background: "var(--border, #cbd5e1)", margin: "0 3px" }} />
                <ToolbarButton
                    active={activeFormats.bullet}
                    onClick={() => exec("insertUnorderedList")}
                    title="Bullet List"
                    icon={<MdFormatListBulleted size={16} />}
                />
                <ToolbarButton
                    active={activeFormats.number}
                    onClick={() => exec("insertOrderedList")}
                    title="Numbered List"
                    icon={<MdFormatListNumbered size={16} />}
                />
                <div style={{ width: 1, height: 16, background: "var(--border, #cbd5e1)", margin: "0 3px" }} />
                <ToolbarButton
                    active={false}
                    onClick={() => exec("removeFormat")}
                    title="Clear Formatting"
                    icon={<MdFormatClear size={15} />}
                />
            </div>

            {/* ContentEditable Area with Placeholder */}
            <div style={{ position: "relative", minHeight, flex: 1, cursor: "text" }} onClick={() => editorRef.current?.focus()}>
                {showPlaceholder && (
                    <div
                        style={{
                            position: "absolute",
                            top: "7px",
                            left: "10px",
                            color: "var(--text-muted, #94a3b8)",
                            fontSize: "13px",
                            pointerEvents: "none",
                            userSelect: "none",
                        }}
                    >
                        {placeholder}
                    </div>
                )}
                <div
                    ref={editorRef}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onKeyUp={updateActiveFormats}
                    onMouseUp={updateActiveFormats}
                    onFocus={() => {
                        setFocused(true);
                        updateActiveFormats();
                    }}
                    onBlur={() => {
                        setFocused(false);
                        handleInput();
                    }}
                    style={{
                        minHeight,
                        padding: "7px 10px",
                        outline: "none",
                        fontSize: "13px",
                        lineHeight: 1.5,
                        color: "var(--text, #111827)",
                    }}
                />
            </div>
        </div>
    );
}

// ─── Toolbar button helper ──────────────────────────────────────────────────
function ToolbarButton({ active, onClick, title, icon }) {
    return (
        <button
            type="button"
            title={title}
            onMouseDown={(e) => {
                // Important: preventDefault avoids editor losing focus
                e.preventDefault();
                onClick();
            }}
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 26,
                height: 24,
                border: "none",
                borderRadius: 4,
                background: active ? "var(--primary, #003b67)" : "transparent",
                color: active ? "#ffffff" : "#475569",
                cursor: "pointer",
                padding: 0,
                transition: "all 0.15s ease",
            }}
            onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = "#e2e8f0";
            }}
            onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
            }}
        >
            {icon}
        </button>
    );
}
